import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useMediaQuery,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import { signUpThunk, updateTokens, clearError, clearSuccess } from '../features/auth/authSlice';
import { useNavigate } from 'react-router-dom';
import {bookAppointment} from '../features/booking/bookingSlice';
import axiosInstance from '../api/axiosInstance';
import { getBookingData, getPendingBooking, clearPendingBooking, clearBookingData } from '../utils/bookingStorage';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  background: theme.palette.background.paper,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    transition: theme.transitions.create(['box-shadow', 'border-color']),
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5),
  textTransform: 'none',
  fontSize: '1.1rem',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const SignUp = () => {
  const [role, setRole] = useState('');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [error, setError] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { loading, errorMessage, successMessage } = useSelector((state) => state.auth);

  useEffect(() => {
    const savedBookingData = getBookingData();
    if (savedBookingData) {
      setRole('patient');
    }
  }, []);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearSuccess());
    }
  }, [successMessage, dispatch]);

  useEffect(() => {
    if (errorMessage) {
      if (typeof errorMessage === 'object') {
        Object.keys(errorMessage).forEach((key) => {
          toast.error(`${key}: ${errorMessage[key].join(', ')}`);
        });
      } else {
        toast.error(errorMessage);
      }
      dispatch(clearError());
    }
  }, [errorMessage, dispatch]);

  const validateForm = () => {
    if (!name || !contact || !password || !password2 || !role) {
      setError('Please fill in all the fields so we can help you better.');
      return false;
    }

    if (!validateContact(contact)) {
      setError('Please enter a valid email or phone number.');
      return false;
    }

    if (password.length < 8) {
      setError('Your password should be at least 8 characters long.');
      return false;
    }

    if (!/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Your password should have at least one uppercase letter and one number.');
      return false;
    }

    if (password !== password2) {
      setError("Passwords don't match - let's try again.");
      return false;
    }

    setError('');
    return true;
  };

  const handleSignUp = async () => {
    if (!validateForm()) return;

    try {
      const response = await dispatch(signUpThunk({
        name: name,
        contact: contact,
        email: contact,
        phone: contact,
        password: password,
        password2: password2,
        role: role,
      })).unwrap();

      const { access, refresh, user } = response;
      dispatch(updateTokens({ accessToken: access, refreshToken: refresh, user }));

      // Check if there's saved booking data or pending booking
      const savedBookingData = getBookingData();
      const pendingBooking = getPendingBooking();
      
      // Handle pending booking from QuickBookingWidget
      if (pendingBooking) {
        try {
          const doctorId = pendingBooking.doctor_id || pendingBooking.doctorId;
          const bookingResponse = await axiosInstance.post(
            `/doctors/${doctorId}/book/`,
            {
              scheduled_date: pendingBooking.appointmentData?.scheduled_date || pendingBooking.selectedDate,
              scheduled_time: pendingBooking.appointmentData?.scheduled_time || pendingBooking.selectedTime,
              type: pendingBooking.appointmentData?.type || (pendingBooking.consultationType === 'video' ? 'Video' : pendingBooking.consultationType === 'phone' ? 'Phone' : 'In-Person'),
              details: pendingBooking.appointmentData?.details || pendingBooking.reason || '',
              // Include clinic ID if available
              ...(pendingBooking.clinic_id && { clinic_id: pendingBooking.clinic_id }),
            }
          );
          
          clearPendingBooking();
          toast.success('Welcome! Your appointment has been booked.');
          
          setTimeout(() => {
            if (user.role === 'patient') {
              navigate('/patient/appointments');
            } else {
              navigate(`/${user.role}-dashboard`);
            }
          }, 1500);
          return;
        } catch (bookingErr) {
          console.error('Booking error after signup:', bookingErr);
          toast.error('Failed to complete booking. Please try again.');
          clearPendingBooking();
        }
      }
      
      // Handle saved booking data from BookAppointment page
      if (savedBookingData) {
        // If there's saved booking data, proceed with booking
        dispatch(
          bookAppointment({
            doctorId: savedBookingData.selectedDoctor,
            clinicId: savedBookingData.selectedClinic || null,
            scheduledDate: savedBookingData.selectedDate,
            scheduledTime: convertTo24Hour(savedBookingData.selectedSlot),
          })
        ).then((result) => {
          if (result.meta.requestStatus === 'fulfilled') {
            clearBookingData();
            toast.success('Welcome! Your appointment has been booked.');
            setTimeout(() => {
              navigate(`/${user.role}-dashboard`);
            }, 2000);
          }
        });
      } else {
        // If no booking data, check if doctor needs onboarding
        if (user.role === 'doctor') {
          toast.success('Welcome! Let\'s complete your profile setup.');
          setTimeout(() => {
            navigate('/doctor-onboarding');
          }, 1000);
        } else {
          // Check if patient needs onboarding
          if (user.role === 'patient') {
            toast.success('Welcome! Let\'s complete your profile setup.');
            setTimeout(() => {
              navigate('/onboarding/patient/');
            }, 1000);
          } else {
            // Regular signup flow for other roles
            toast.success('Welcome to PsychePoint!');
            setTimeout(() => {
              navigate(`/${user.role}-dashboard`);
            }, 1000);
          }
        }
      }
    } catch (err) {
      console.error('Signup failed:', err);
      if (typeof err === 'object') {
        Object.keys(err).forEach((key) => {
          toast.error(`${key}: ${err[key].join(', ')}`);
        });
      } else {
        toast.error(err || 'Signup failed. Please try again.');
      }
    }
  };

  const convertTo24Hour = (time) => {
    if (!time) return '';
    const [hour, minute] = time.split(/[: ]/);
    const period = time.includes('PM') ? 'PM' : 'AM';
    let hours24 = parseInt(hour, 10);
    if (period === 'PM' && hours24 !== 12) {
      hours24 += 12;
    }
    if (period === 'AM' && hours24 === 12) {
      hours24 = 0;
    }
    return `${hours24.toString().padStart(2, '0')}:${minute}`;
  };

  const validateContact = (input) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    return emailRegex.test(input) || phoneRegex.test(input);
  };

  const getSteps = () => {
    return ['Role', 'Details', 'Security'];
  };

  const [activeStep, setActiveStep] = useState(0);
  const steps = getSteps();

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSignUp();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, sm: 4, md: 6 }, mb: 4 }}>
      <Fade in timeout={1000}>
        <Box>
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            gutterBottom 
            align="center"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 4
            }}
          >
            Let's Get Started 🚀
          </Typography>

          <StyledPaper elevation={0}>
            {error && (
              <Fade in>
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      fontSize: '1.5rem'
                    }
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            <Stepper 
              activeStep={activeStep} 
              alternativeLabel={!isMobile}
              orientation={isMobile ? 'vertical' : 'horizontal'}
              sx={{ mb: 4 }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Fade in timeout={500}>
                <Box>
                  {activeStep === 0 && (
                    <FormControl fullWidth>
                      <InputLabel>Your Role</InputLabel>
                      <Select 
                        value={role} 
                        onChange={(e) => setRole(e.target.value)}
                        sx={{ borderRadius: 3 }}
                      >
                        <MenuItem value="patient">🧑‍💼 Seeking Support</MenuItem>
                        {/* <MenuItem value="doctor">👨‍⚕️ Health Professional</MenuItem>
                        <MenuItem value="receptionist">👩‍💼 Clinic Staff</MenuItem>
                        <MenuItem value="pharmacist">👨‍⚕️ Pharmacy Staff</MenuItem> */}
                        {/* <MenuItem value="caregiver">🤝 Supporting Someone</MenuItem> */}
                      </Select>
                    </FormControl>
                  )}

                  {activeStep === 1 && (
                    <>
                      <StyledTextField
                        label="Your Name"
                        variant="outlined"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <StyledTextField
                        label="Email or Phone"
                        variant="outlined"
                        fullWidth
                        value={contact}
                        onChange={(e) => setContact(e.target.value)}
                      />
                    </>
                  )}

                  {activeStep === 2 && (
                    <>
                      <StyledTextField
                        label="Create a Password"
                        type="password"
                        variant="outlined"
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <StyledTextField
                        label="Confirm Password"
                        type="password"
                        variant="outlined"
                        fullWidth
                        value={password2}
                        onChange={(e) => setPassword2(e.target.value)}
                      />
                    </>
                  )}
                </Box>
              </Fade>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                {activeStep > 0 && (
                  <StyledButton
                    variant="outlined"
                    onClick={handleBack}
                    sx={{ flex: 1 }}
                  >
                    Back
                  </StyledButton>
                )}
                <StyledButton
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading}
                  sx={{ 
                    flex: 1,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : activeStep === steps.length - 1 ? (
                    'Create My Account'
                  ) : (
                    'Next'
                  )}
                </StyledButton>
              </Box>
            </Box>
          </StyledPaper>
        </Box>
      </Fade>

      <ToastContainer 
        position="bottom-center" 
        autoClose={3000}
        theme="colored"
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Container>
  );
};

export default SignUp;
